import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import SocialIcons from "@/components/SocialIcons";
import { WhatsAppCTA } from "@/components/WhatsApp";
import { SITE, DEFAULT_WA_MESSAGES, WA_LABELS } from "@/config/site";
import { API } from "@/lib/api";
import { MessageCircle, Mail, Phone, Calendar, MapPin, Clock, Info, Send, CheckCircle2, ArrowRight, UserRound } from "lucide-react";
import { usePageCms } from "@/lib/siteSettings";

const TOPIC_IDS = ["assessment", "injury", "membership", "remote", "corporate", "professional", "other"];
const NO_PREFERENCE = "";

const initialForm = { name: "", email: "", phone: "", topic: "assessment", therapist: NO_PREFERENCE, message: "", consent: false };

export default function Contact() {
  const { t } = useTranslation();
  const cms = usePageCms("contact");
  const location = useLocation();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [team, setTeam] = useState([]);

  // Fetch the published team so the "Preferred therapist" dropdown mirrors
  // the /team page. Falls back to no-list gracefully if the endpoint fails.
  useEffect(() => {
    axios.get(`${API}/public/cms/team`)
      .then((r) => setTeam(r.data || []))
      .catch(() => setTeam([]));
  }, []);

  // Pre-fill the therapist from ?therapist=Full+Name (used by "Book with X"
  // buttons on the /team page). Runs once after the team list loads so the
  // <select> can be matched against a known name.
  const requestedTherapist = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return (p.get("therapist") || "").trim();
  }, [location.search]);

  useEffect(() => {
    if (!requestedTherapist || !team.length) return;
    // Only pre-fill if the requested therapist is actually in the current
    // published roster — avoids silent mismatches with stale URLs.
    const match = team.find((m) => m.name === requestedTherapist);
    if (match) {
      setForm((f) => (f.therapist ? f : { ...f, therapist: match.name }));
    }
  }, [requestedTherapist, team]);

  // Prefill topic + message from the Service Finder result deep-link:
  //   /contact?topic=finder&pathway=X&summary=Y
  // The pathway id + answer summary become the opening lines of the message
  // so the clinician sees the exact context the finder used.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const topic = p.get("topic");
    const pathway = p.get("pathway");
    const summary = p.get("summary");
    if (!topic && !pathway && !summary) return;
    setForm((f) => {
      if (f.message || (topic && f.topic !== "assessment")) return f; // don't overwrite user typing
      const lines = [];
      if (pathway) lines.push(`Recommended pathway from the Service Finder: ${pathway}.`);
      if (summary) lines.push(`My answers: ${summary}.`);
      lines.push(""); // blank line for the user to continue typing
      return {
        ...f,
        topic: topic === "finder" ? "assessment" : (f.topic || "assessment"),
        message: lines.join("\n"),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t("pub.contact.errorRequired"));
      return;
    }
    if (!form.consent) {
      toast.error(t("pub.contact.errorConsent"));
      return;
    }
    setSubmitting(true);
    try {
      // If a therapist is selected we prepend it to the message so it lands in
      // any downstream inbox / CRM without a schema change on the backend.
      const finalMessage = form.therapist
        ? `Preferred therapist: ${form.therapist}\n\n${form.message}`
        : form.message;
      await axios.post(`${API}/contact/inquiry`, {
        name: form.name, email: form.email, phone: form.phone || null,
        topic: form.topic, message: finalMessage, consent: form.consent, source: "contact_page",
      });
      setSent(true);
      toast.success(t("pub.contact.successToast"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pub.contact.errorNetwork"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen fl-page" style={{ background: "var(--fl-bg)", color: "var(--fl-text)" }}>
      <PublicHeader />

      {/* Hero */}
      {cms.isVisible("hero") && (
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 px-6 bg-[#0A0A0A] fl-noise relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center relative">
          <div className="fl-label text-[color:var(--fl-orange)] mb-3">{cms.text("hero", "eyebrow", t("pub.contact.eyebrow"))}</div>
          <h1 className="fl-heading text-5xl md:text-6xl leading-none">
            {cms.get("hero").heading ? cms.get("hero").heading : (<>{t("pub.contact.h1a")} <span className="text-[color:var(--fl-orange)]">{t("pub.contact.h1b")}</span> {t("pub.contact.h1c")}</>)}
          </h1>
          <p className="mt-5 text-base md:text-lg max-w-2xl mx-auto" style={{ color: "var(--fl-muted)" }}>
            {cms.text("hero", "body", t("pub.contact.subtitle"))}
          </p>
        </div>
      </section>
      )}

      {/* Three primary contact cards */}
      <section className="py-20 md:py-28 px-6 bg-[#121212] border-t border-white/5">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="fl-card p-6" data-testid="contact-card-whatsapp">
            <MessageCircle size={22} className="text-[#25D366]" />
            <div className="fl-heading text-lg mt-3">{t("pub.contact.waTitle")}</div>
            <div className="text-xs mt-1 mb-4" style={{ color: "var(--fl-muted)" }}>{t("pub.contact.waDesc")}</div>
            <WhatsAppCTA message={DEFAULT_WA_MESSAGES.contact} testid="contact-wa-primary" fullWidth>
              {t("pub.wa.message")}
            </WhatsAppCTA>
          </div>

          <div className="fl-card p-6" data-testid="contact-card-book">
            <Calendar size={22} className="text-[color:var(--fl-orange)]" />
            <div className="fl-heading text-lg mt-3">{t("pub.contact.bookTitle")}</div>
            <div className="text-xs mt-1 mb-4" style={{ color: "var(--fl-muted)" }}>{t("pub.contact.bookDesc")}</div>
            <div className="flex flex-col gap-2">
              <WhatsAppCTA message={DEFAULT_WA_MESSAGES.bookAssessment} testid="contact-wa-book" variant="soft" fullWidth>
                {t("pub.wa.book")}
              </WhatsAppCTA>
              <Link to="/register" className="fl-btn-ghost text-sm px-3 py-2 rounded-md text-center" data-testid="contact-create-account">
                {t("pub.contact.createAcct")}
              </Link>
            </div>
          </div>

          <div className="fl-card p-6" data-testid="contact-card-email">
            <Mail size={22} className="text-[color:var(--fl-orange)]" />
            <div className="fl-heading text-lg mt-3">{t("pub.contact.emailTitle")}</div>
            <div className="text-xs mt-1 mb-4" style={{ color: "var(--fl-muted)" }}>{t("pub.contact.emailDesc")}</div>
            <a href={`mailto:${SITE.email}`} className="fl-btn text-sm px-3 py-2 rounded-md inline-flex items-center gap-2 w-full justify-center" data-testid="contact-email-link">
              <Mail size={14} /> {SITE.email}
            </a>
          </div>
        </div>
      </section>

      {/* Main content: form + sidebar */}
      <section className="py-24 md:py-32 px-6 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className="fl-card p-6 md:p-8 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,69,0,0.10), transparent 70%)" }} />
              <div className="relative">
                <div className="fl-label text-[color:var(--fl-orange)] mb-2">{t("pub.contact.formEyebrow")}</div>
                <h2 className="fl-heading text-3xl">{t("pub.contact.formTitle")}</h2>
                <p className="mt-2 text-sm" style={{ color: "var(--fl-muted)" }}>{t("pub.contact.formSub")}</p>

                {sent ? (
                  <div className="mt-6 p-5 rounded-md border" style={{ borderColor: "#22C55E55", background: "#22C55E10" }} data-testid="contact-form-success">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="text-[#22C55E] flex-shrink-0 mt-0.5" size={20} />
                      <div className="text-sm" style={{ color: "var(--fl-text)" }}>
                        <b>{t("pub.contact.successTitle")}</b>
                        <div className="mt-1" style={{ color: "var(--fl-muted)" }}>{t("pub.contact.successBody")}</div>
                        {form.therapist && (
                          <div className="mt-2 inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full" style={{ background: "color-mix(in oklab, var(--fl-orange) 14%, transparent)", color: "var(--fl-orange)", border: "1px solid color-mix(in oklab, var(--fl-orange) 30%, transparent)" }}>
                            <UserRound size={11} /> Preferred therapist: {form.therapist}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Direct-contact shortcuts — WhatsApp (in-app CTA) or your default mail client. */}
                    <div className="mt-4 grid sm:grid-cols-2 gap-2" data-testid="contact-success-actions">
                      <a
                        href={`mailto:${SITE.email}?subject=${encodeURIComponent("Follow-up on my enquiry · " + (form.topic || "general"))}&body=${encodeURIComponent(`Hi Functional LAB team,\n\nI just submitted an enquiry through the website and wanted to follow up directly.\n\nName: ${form.name}\nTopic: ${form.topic}\n\nMy message:\n${form.message}\n\nThank you.`)}`}
                        className="fl-btn-ghost text-sm px-3 py-2.5 rounded-md inline-flex items-center gap-2 justify-center"
                        data-testid="contact-success-email"
                      >
                        <Mail size={14} /> {t("pub.contact.successEmailCta", "Continue via email")}
                      </a>
                      <WhatsAppCTA
                        message={`Hi Functional LAB, I just submitted an enquiry (${form.topic || "general"}) as ${form.name} <${form.email}>. Following up on WhatsApp — ${(form.message || "").slice(0, 220)}`}
                        variant="soft"
                        size="sm"
                        fullWidth
                        testid="contact-success-wa"
                      >
                        {t("pub.contact.successWaCta", "Continue on WhatsApp")}
                      </WhatsAppCTA>
                    </div>
                    <div className="mt-3">
                      <button type="button" onClick={() => { setSent(false); setForm(initialForm); }} className="text-xs underline underline-offset-2" style={{ color: "var(--fl-muted)" }} data-testid="contact-success-reset">
                        {t("pub.contact.sendAnother", "Send another message")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={submit} className="mt-6 space-y-4" data-testid="contact-form">
                    {requestedTherapist && form.therapist && (
                      <div
                        className="flex items-start gap-2 rounded-md px-3 py-2 text-xs"
                        style={{ background: "color-mix(in oklab, var(--fl-orange) 10%, transparent)", border: "1px solid color-mix(in oklab, var(--fl-orange) 30%, transparent)", color: "var(--fl-text)" }}
                        data-testid="contact-therapist-prefilled"
                      >
                        <UserRound size={14} className="mt-0.5 text-[color:var(--fl-orange)]" />
                        <span>
                          You're booking with <b>{form.therapist}</b>. You can change or clear this below.
                        </span>
                      </div>
                    )}
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs mb-1 block" style={{ color: "var(--fl-muted)" }}>{t("pub.contact.fName")}</label>
                        <input type="text" required value={form.name} onChange={(e) => update("name", e.target.value)} className="fl-input w-full text-sm px-3 py-2 rounded-md" data-testid="contact-name" />
                      </div>
                      <div>
                        <label className="text-xs mb-1 block" style={{ color: "var(--fl-muted)" }}>{t("pub.contact.fEmail")}</label>
                        <input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="fl-input w-full text-sm px-3 py-2 rounded-md" data-testid="contact-email-input" />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs mb-1 block" style={{ color: "var(--fl-muted)" }}>{t("pub.contact.fPhone")}</label>
                        <input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="fl-input w-full text-sm px-3 py-2 rounded-md" data-testid="contact-phone" />
                      </div>
                      <div>
                        <label className="text-xs mb-1 block" style={{ color: "var(--fl-muted)" }}>{t("pub.contact.fTopic")}</label>
                        <select value={form.topic} onChange={(e) => update("topic", e.target.value)} className="fl-input w-full text-sm px-3 py-2 rounded-md" data-testid="contact-topic">
                          {TOPIC_IDS.map((id) => <option key={id} value={id}>{t(`pub.contact.topics.${id}`)}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs mb-1 block flex items-center gap-1.5" style={{ color: "var(--fl-muted)" }}>
                        <UserRound size={12} /> Preferred therapist <span style={{ color: "var(--fl-muted-2)" }}>· optional</span>
                      </label>
                      <select
                        value={form.therapist}
                        onChange={(e) => update("therapist", e.target.value)}
                        className="fl-input w-full text-sm px-3 py-2 rounded-md"
                        data-testid="contact-therapist"
                      >
                        <option value="">No preference — assign the best fit</option>
                        {team.map((m) => (
                          <option key={m.id} value={m.name}>
                            {m.name}{m.role ? ` — ${m.role}` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs mb-1 block" style={{ color: "var(--fl-muted)" }}>{t("pub.contact.fMessage")}</label>
                      <textarea required rows={6} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder={t("pub.contact.fMessagePh")} className="fl-input w-full text-sm px-3 py-2 rounded-md resize-y" data-testid="contact-message" />
                    </div>

                    <label className="flex items-start gap-2 text-xs" style={{ color: "var(--fl-muted)" }}>
                      <input type="checkbox" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} data-testid="contact-consent" className="mt-0.5" />
                      {t("pub.contact.fConsent")}
                    </label>

                    <div className="flex flex-wrap gap-3 items-center">
                      <button type="submit" disabled={submitting} className="fl-btn text-sm" data-testid="contact-submit">
                        <Send size={14} /> {submitting ? t("pub.contact.submitting") : t("pub.contact.submit")}
                      </button>
                      <WhatsAppCTA message={DEFAULT_WA_MESSAGES.contact} testid="contact-form-wa" variant="soft" size="sm">
                        {t("pub.contact.orWa")}
                      </WhatsAppCTA>
                    </div>

                    <div className="text-[11px] flex items-start gap-1" style={{ color: "var(--fl-muted-2)" }}>
                      <Info size={11} className="mt-0.5 flex-shrink-0" />
                      <span>{t("pub.contact.sensitive")}</span>
                    </div>
                  </form>
                )}
              </div>
            </div>

            <div className="mt-4 fl-card p-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="fl-label" style={{ color: "var(--fl-orange)" }}>{t("pub.contact.notSureEyebrow")}</div>
                <div className="text-sm mt-1" style={{ color: "var(--fl-muted)" }}>{t("pub.contact.notSureBody")}</div>
              </div>
              <Link to="/service-finder" className="fl-btn text-sm" data-testid="contact-sf-link">
                {t("pub.contact.notSureCta")} <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="fl-card p-5">
              <div className="fl-heading text-base mb-3">{t("pub.contact.directLines")}</div>
              <div className="space-y-2 text-sm">
                <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-[color:var(--fl-orange)]" data-testid="contact-tel">
                  <Phone size={14} /> {SITE.phone}
                </a>
                <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-[color:var(--fl-orange)]" data-testid="contact-email">
                  <Mail size={14} /> {SITE.email}
                </a>
              </div>
              <div className="mt-4">
                <SocialIcons showLabels testid="contact-socials" />
              </div>
            </div>

            <div className="fl-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={16} className="text-[color:var(--fl-orange)]" />
                <div className="fl-heading text-base">{t("pub.contact.location")}</div>
              </div>
              <div className="text-sm" style={{ color: "var(--fl-muted)" }}>
                <b style={{ color: "var(--fl-text)" }}>{SITE.address.line1}</b>
                {SITE.address.line2 && <div>{SITE.address.line2}</div>}
                <div>{SITE.address.city}{SITE.address.postcode ? `, ${SITE.address.postcode}` : ""}</div>
              </div>
            </div>

            <div className="fl-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={16} className="text-[color:var(--fl-orange)]" />
                <div className="fl-heading text-base">{t("pub.contact.hours")}</div>
              </div>
              <div className="text-sm space-y-1" style={{ color: "var(--fl-muted)" }}>
                {SITE.hours.map((h) => (
                  <div key={h.day} className="flex justify-between gap-3">
                    <span>{h.day}</span>
                    <span style={{ color: "var(--fl-text)" }}>{h.hours}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-[11px] flex items-start gap-1" style={{ color: "var(--fl-muted-2)" }}>
                <Info size={11} className="mt-0.5 flex-shrink-0" />
                <span>{SITE.hoursNote}</span>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
